import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, X, CheckCheck, Trash2, Search,
  Inbox, Filter
} from 'lucide-react';
import { useNotificationStore } from '../../lib/stores/notificationStore';
import { NotificationItem, NotificationGroup } from '../notifications/NotificationItem';
import type { Notification } from '../../lib/types';

type FilterTab = 'all' | 'unread' | 'read';

export function NotificationDropdown({triggerIcon}:{triggerIcon?:React.ReactNode}={}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    notifications,
    whatsappNotifications,
    unreadCount,
    whatsappUnreadCount,
    selectedIds,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    markAllWhatsAppAsRead,
    deleteNotification,
    toggleSelection,
    clearSelection,
    bulkMarkAsRead,
    bulkDelete,
    getNotificationsGroupedByDate
  } = useNotificationStore();

  // Calcola il totale complessivo di notifiche non lette
  const safeUnreadCount = typeof unreadCount === 'number' ? unreadCount : 0;
  const safeWhatsappUnreadCount = typeof whatsappUnreadCount === 'number' ? whatsappUnreadCount : 0;
  const totalUnreadCount = safeUnreadCount + safeWhatsappUnreadCount;

  // Combina tutte le notifiche
  const allNotifications = useMemo(() => {
    const combined = [
      ...notifications.map(n => ({ ...n, isWhatsApp: false })),
      ...whatsappNotifications.map(n => ({ ...n, isWhatsApp: true }))
    ];
    return combined.sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [notifications, whatsappNotifications]);

  // Filtra per tab e ricerca
  const filteredNotifications = useMemo(() => {
    let filtered = allNotifications;

    // Filtro per tab
    if (activeTab === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (activeTab === 'read') {
      filtered = filtered.filter(n => n.read);
    }

    // Filtro per ricerca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(n =>
        n.title.toLowerCase().includes(query) ||
        n.message.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allNotifications, activeTab, searchQuery]);

  // Raggruppa per data
  const groupedNotifications = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(thisWeekStart.getDate() - 7);

    const groups: { [key: string]: typeof filteredNotifications } = {
      'Oggi': [],
      'Ieri': [],
      'Questa settimana': [],
      'Più vecchie': []
    };

    filteredNotifications.forEach(notification => {
      const notificationDate = new Date(notification.created_at);
      notificationDate.setHours(0, 0, 0, 0);

      if (notificationDate.getTime() === today.getTime()) {
        groups['Oggi'].push(notification);
      } else if (notificationDate.getTime() === yesterday.getTime()) {
        groups['Ieri'].push(notification);
      } else if (notificationDate.getTime() >= thisWeekStart.getTime()) {
        groups['Questa settimana'].push(notification);
      } else {
        groups['Più vecchie'].push(notification);
      }
    });

    // Rimuovi gruppi vuoti
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }, [filteredNotifications]);

  // Contatori per tab
  const tabCounts = useMemo(() => ({
    all: allNotifications.length,
    unread: allNotifications.filter(n => !n.read).length,
    read: allNotifications.filter(n => n.read).length
  }), [allNotifications]);

  // Ascolta eventi per aggiornamenti badge WhatsApp
  useEffect(() => {
    const handleBadgeUpdate = () => {
      useNotificationStore.getState().fetchNotifications();
    };
    window.addEventListener('whatsapp-badge-update', handleBadgeUpdate);
    return () => window.removeEventListener('whatsapp-badge-update', handleBadgeUpdate);
  }, []);

  // Blocca scroll del body quando il drawer è aperto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Chiudi con ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isClosing) closeDrawer();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, isClosing]);

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    markAllWhatsAppAsRead();
  };

  const openDrawer = () => {
    useNotificationStore.getState().fetchNotifications();
    setIsClosing(false);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsClosing(true);
    clearSelection();
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      closeDrawer();
    }
  };

  const handleBulkMarkAsRead = () => {
    const ids = Array.from(selectedIds);
    if (ids.length > 0) {
      bulkMarkAsRead(ids);
    }
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length > 0) {
      bulkDelete(ids);
    }
  };

  const hasSelection = selectedIds.size > 0;

  return (
    <>
      {/* Trigger Button */}
      <button
        className="relative flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-accent text-foreground transition-colors"
        onClick={openDrawer}
        data-tooltip-id="header-tooltip"
        data-tooltip-content="Notifiche"
        aria-label="Notifiche"
      >
        {triggerIcon||<Bell className="h-5 w-5 text-[#E91E8C]" />}
        {totalUnreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[14px] h-3.5 px-1 text-[10px] font-semibold text-white bg-fuchsia-500 rounded-full">
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </span>
        )}
      </button>

      {/* Overlay + Drawer - Renderizzato nel body via Portal */}
      {isOpen && createPortal(
        <>
          {/* Overlay scuro */}
          <div
            className={`fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm duration-300 ${
              isClosing ? 'animate-out fade-out-0' : 'animate-in fade-in-0'
            }`}
            onClick={closeDrawer}
          />

          {/* Drawer */}
          <div className={`fixed top-0 right-0 bottom-0 z-[9999] w-full max-w-md bg-background border-l border-border shadow-2xl duration-300 flex flex-col ${
            isClosing ? 'animate-out slide-out-to-right' : 'animate-in slide-in-from-right'
          }`}>
            {/* Header */}
            <div className="flex-shrink-0 border-b border-border">
              {/* Titolo e azioni */}
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Inbox className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Notifiche</h2>
                    {totalUnreadCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {totalUnreadCount} non lett{totalUnreadCount === 1 ? 'a' : 'e'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {totalUnreadCount > 0 && !hasSelection && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                      title="Segna tutte come lette"
                    >
                      <CheckCheck className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={closeDrawer}
                    className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Bulk Actions Bar - visibile quando ci sono selezioni */}
              {hasSelection && (
                <div className="px-4 py-2 bg-primary/5 border-t border-border flex items-center justify-between">
                  <span className="text-sm text-foreground">
                    {selectedIds.size} selezionat{selectedIds.size === 1 ? 'a' : 'e'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleBulkMarkAsRead}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1.5"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Segna lette
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors flex items-center gap-1.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Elimina
                    </button>
                    <button
                      onClick={clearSelection}
                      className="px-3 py-1.5 text-xs font-medium rounded-md hover:bg-accent text-muted-foreground transition-colors"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              )}

              {/* Tabs di filtro */}
              <div className="px-4 py-2 flex items-center gap-1">
                {(['all', 'unread', 'read'] as FilterTab[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`
                      px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200
                      ${activeTab === tab
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                      }
                    `}
                  >
                    {tab === 'all' && `Tutte (${tabCounts.all})`}
                    {tab === 'unread' && `Non lette (${tabCounts.unread})`}
                    {tab === 'read' && `Lette (${tabCounts.read})`}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="px-4 pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Cerca notifiche..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Lista notifiche raggruppate */}
            <div className="flex-1 overflow-y-auto">
              {Object.keys(groupedNotifications).length > 0 ? (
                Object.entries(groupedNotifications).map(([groupTitle, groupNotifications]) => (
                  <NotificationGroup
                    key={groupTitle}
                    title={groupTitle}
                    notifications={groupNotifications}
                    onMarkAsRead={markAsRead}
                    onDelete={deleteNotification}
                    onClick={handleNotificationClick}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelection}
                    showCheckbox={true}
                    defaultExpanded={groupTitle === 'Oggi' || groupTitle === 'Ieri'}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-16">
                  <div className="p-4 rounded-full bg-muted/50 mb-4">
                    <Bell className="h-10 w-10 text-muted-foreground/40" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {searchQuery ? 'Nessun risultato' : 'Nessuna notifica'}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1 text-center px-8">
                    {searchQuery
                      ? `Nessuna notifica trovata per "${searchQuery}"`
                      : activeTab === 'unread'
                        ? 'Tutte le notifiche sono state lette'
                        : 'Le tue notifiche appariranno qui'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            {allNotifications.length > 0 && (
              <div className="flex-shrink-0 px-4 py-3 border-t border-border bg-muted/30">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    closeDrawer();
                  }}
                  className="w-full py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors rounded-lg hover:bg-accent"
                >
                  Vedi tutte nel Centro Notifiche
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
